import { type Href } from 'expo-router';
import { View } from 'react-native';

import { Button } from '@/components/ui/Button';
import { Surface } from '@/components/ui/Surface';
import { FREE_RUN_LIMIT, FREE_TEMPLATE_LIMIT, PLANS, TRIAL_LINE } from '@/lib/billing';

import { Body } from './Body';
import { BodySemibold } from './BodySemibold';
import { Display } from './Display';
import { Eyebrow } from './Eyebrow';
import { PriceTick } from './PriceTick';
import { Section } from './Section';

const FREE_FEATURES = [
  `${FREE_TEMPLATE_LIMIT} checklists, ${FREE_RUN_LIMIT} runs`,
  'Shared runs & attribution',
  'Offline & sealed logs',
];
const PRO_FEATURES = [
  'Unlimited checklists & runs',
  'Shared runs stay live',
  'Priority sync & export',
];

export function Pricing({ getStartedHref }: { getStartedHref: Href }) {
  const annual = PLANS.find((p) => p.id === 'annual') ?? PLANS[0]!;
  const monthly = PLANS.find((p) => p.id === 'monthly') ?? PLANS[1]!;

  return (
    <Section className="py-20" innerClassName="max-w-[920px] self-center">
      <View className="mb-12 items-center gap-4">
        <Eyebrow className="text-xs tracking-[0.14em] text-signal">The pricing</Eyebrow>
        <Display className="max-w-[560px] text-center text-[38px] leading-[1.08] tracking-[-0.02em] text-foreground">
          Start free, no card required.
        </Display>
      </View>

      <View className="flex-row flex-wrap items-stretch justify-center gap-5">
        {/* Free */}
        <Surface variant="card" className="min-w-[280px] flex-1 rounded-[22px] p-8">
          <Eyebrow className="mb-3.5 text-[11px] tracking-[0.16em] text-faint">
            Free
          </Eyebrow>
          <Display className="mb-1.5 text-[46px] tracking-[-0.02em] text-foreground">
            $0
          </Display>
          <Body className="mb-6 text-sm leading-[1.55] text-foreground-secondary">
            Everything you need to run real checklists, solo or shared.
          </Body>
          <View className="mb-7 gap-3">
            {FREE_FEATURES.map((label) => (
              <View key={label} className="flex-row items-center gap-3">
                <PriceTick />
                <Body className="text-[14.5px] text-foreground">{label}</Body>
              </View>
            ))}
          </View>
          <Button
            block
            variant="outline"
            label="Get started"
            href={getStartedHref}
            className="mt-auto"
          />
        </Surface>

        {/* Pro */}
        <Surface variant="ink" className="min-w-[280px] flex-1 rounded-[22px] p-8">
          <View className="absolute right-6 top-6 rounded-full bg-signal px-2.5 py-1">
            <Eyebrow className="text-[10px] tracking-[0.12em] text-on-signal">
              Unlimited
            </Eyebrow>
          </View>
          <Eyebrow className="mb-3.5 text-[11px] tracking-[0.16em] text-signal">
            Pro
          </Eyebrow>
          <Display className="mb-2 text-[46px] tracking-[-0.02em] text-on-ink">
            {annual.price}
          </Display>
          <Body className="mb-1.5 text-[13.5px] text-faint">
            or{' '}
            <BodySemibold className="text-[13.5px] text-on-ink">
              {monthly.price}
            </BodySemibold>
          </Body>
          <Body className="mb-6 text-sm leading-[1.55] text-faint">
            For teams that live in checklists. Every limit lifted.
          </Body>
          <View className="mb-7 gap-3">
            {PRO_FEATURES.map((label) => (
              <View key={label} className="flex-row items-center gap-3">
                <PriceTick accent />
                <Body className="text-[14.5px] text-on-ink">{label}</Body>
              </View>
            ))}
          </View>
          <Button block label="Go Pro" href={getStartedHref} className="mt-auto" />
          <Body className="mt-3 text-center text-sm text-faint">{TRIAL_LINE}</Body>
        </Surface>
      </View>
    </Section>
  );
}
